import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * MobileCollapse — Section repliable sur mobile, toujours visible sur desktop.
 *
 * Usage :
 *   <MobileCollapse title="Politique de conservation" defaultOpen={false}>
 *     <p>...contenu long...</p>
 *   </MobileCollapse>
 *
 * Comportement :
 *   - Mobile (< md) : bouton header cliquable avec Chevron animé. Contenu caché par défaut.
 *   - Desktop (≥ md) : header visible (h2/h3 stylisé), contenu toujours visible. Bouton toggle
 *     reste cliquable si `alwaysInteractive=true` mais par défaut désactivé sur desktop.
 *
 * Props :
 *   - title: titre de la section (string ou ReactNode)
 *   - children: contenu caché/affiché
 *   - defaultOpen: état initial mobile (false par défaut)
 *   - icon: icône lucide optionnelle à gauche du titre
 *   - level: "h2" | "h3" (défaut "h3") — pour l'affichage desktop
 *   - badge: badge optionnel à droite du titre (ex: compteur)
 *   - testId: préfixe data-testid (auto-suffixe -toggle et -content)
 *   - className: classes supplémentaires sur le conteneur
 */
export const MobileCollapse = ({
  title,
  children,
  defaultOpen = false,
  icon: Icon,
  level = 'h3',
  badge,
  testId,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const HeadingTag = level === 'h2' ? 'h2' : 'h3';

  return (
    <section
      className={cn('border-b border-border/60 md:border-0 md:mb-6', className)}
      data-testid={testId}
    >
      {/* Bouton mobile (cliquable) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className={cn(
          'md:hidden w-full flex items-center justify-between py-3 gap-2 text-left',
          'hover:text-[hsl(var(--primary))] transition-colors'
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {Icon && <Icon className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0" />}
          <span className="font-black text-sm leading-tight truncate">{title}</span>
          {badge && (
            <span className="inline-flex items-center rounded-full bg-muted text-foreground/75 text-[10px] font-bold px-2 py-0.5 flex-shrink-0">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown className={cn('w-4 h-4 transition-transform flex-shrink-0', open && 'rotate-180')} />
      </button>

      {/* Header desktop (visible) */}
      <div className="hidden md:flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0" />}
        <HeadingTag
          className={cn(
            'font-black flex-1',
            level === 'h2' ? 'text-xl' : 'text-lg'
          )}
        >
          {title}
        </HeadingTag>
        {badge && (
          <span className="inline-flex items-center rounded-full bg-muted text-foreground/75 text-xs font-bold px-2.5 py-0.5">
            {badge}
          </span>
        )}
      </div>

      {/* Contenu */}
      <div
        className={cn('md:block', open ? 'block pb-4' : 'hidden')}
        data-testid={testId ? `${testId}-content` : undefined}
      >
        {children}
      </div>
    </section>
  );
};

export default MobileCollapse;
